import * as React from 'react';
import { Alert, Button, Modal, Row, Spinner, Stack } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  BookingComputedStatus,
  HostingStatusOption,
  UserBookingComputedStatusOptions,
  BookingExtendedView,
  EventHostView,
} from 'cb-model';
import {
  CalendarView,
  ColourKey,
  Display,
  DisplayFieldBlock,
  selectUser,
  useCalendarRange,
  useCacheQuery,
  useCloneEntity,
} from 'gossamer-front-end';
import { useSelector } from 'react-redux';
import { getFilter } from 'gossamer-unified';
import { formatEventUrl } from 'shared-front-end';
import { UserBookingCalendarEvent } from './CalendarCombined';

export const CalendarDesktop = (): React.JSX.Element => {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const { range } = useCalendarRange();
  const [selectedBooking, setSelectedBooking] = React.useState<UserBookingCalendarEvent | null>(null);
  const [activeColourKeyItems, setActiveColourKeyItems] = React.useState<string[]>(
    UserBookingComputedStatusOptions.map((item) => item.id),
  );

  const LocalBookingExtendedView = useCloneEntity(BookingExtendedView, (local) => {
    Object.values(local.fields).forEach((field) => {
      field.visible = false;
    });
    local.fields.type.visible = true;
    local.fields.start_at.visible = true;
    local.fields.end_at.visible = true;
    local.fields.location_type.visible = true;
    local.fields.location.visible = true;
    local.fields.visibility_limit.visible = true;
    local.fields.visibility_limit.formColumnWidths = { md: 12 };
    local.fields.flow_option.visible = true;
    local.fields.flow_option.formColumnWidths = { md: 12 };
    local.fields.ticket_title.visible = true;
    local.fields.booked_quantity.visible = true;
    local.fields.computed_status.visible = true;
  });

  const bookingsQuery = useCacheQuery(
    BookingExtendedView,
    {
      where: {
        and: [
          getFilter('user_id', 'eq', user?.userId || ''),
          getFilter('booking_status', 'eq', 'active'),
          getFilter('end_at', 'gt', range.start.toISOString()),
          getFilter('start_at', 'lt', range.end.toISOString()),
        ],
      },
      order: [['start_at', 'ASC']],
      limit: 1000,
    },
    !user,
  );

  const hostedEventsQuery = useCacheQuery(
    EventHostView,
    {
      where: {
        and: [
          getFilter('user_id', 'eq', user?.userId || ''),
          getFilter('visibility_limit', 'ne', 'cancelled'),
          getFilter('type', 'ne', 'on-demand'),
          getFilter('end_at', 'gt', range.start.toISOString()),
          getFilter('start_at', 'lt', range.end.toISOString()),
        ],
      },
      limit: 1000,
    },
    !user,
  );

  const events = (bookingsQuery.data || [])
    .filter((booking) => activeColourKeyItems.includes(booking.computed_status.toLowerCase() as BookingComputedStatus))
    .map((booking) => {
      const option = UserBookingComputedStatusOptions.find(
        (item) => item.id === (booking.computed_status.toLowerCase() as BookingComputedStatus),
      );
      return {
        id: String(booking.event_id),
        title: booking.title || booking.ticket_title || `Event ${booking.event_id}`,
        start: new Date(booking.start_at),
        end: new Date(booking.end_at),
        bgColor: option?.bgColor || '#ACBDE9',
        textColor: option?.textColor || '#000000',
        booking,
      } as UserBookingCalendarEvent;
    })
    .concat(
      (hostedEventsQuery.data || [])
        .filter((_hostedEvent) => activeColourKeyItems.includes('hosting'))
        .map((hostedEvent) => ({
          id: String(hostedEvent.event_id),
          title: hostedEvent.title || `Event ${hostedEvent.event_id}`,
          start: new Date(hostedEvent.start_at),
          end: new Date(hostedEvent.end_at),
          bgColor: HostingStatusOption?.bgColor || '#ACBDE9',
          textColor: HostingStatusOption?.textColor || '#000000',
          // hostedEvent,
        })),
    );

  if (!user) {
    return <Alert variant="danger">Login required.</Alert>;
  }

  return (
    <>
      {bookingsQuery.isFetching && <Spinner animation="border" />}
      {bookingsQuery.error && <Alert variant="danger">Unable to load bookings.</Alert>}
      <CalendarView<UserBookingCalendarEvent>
        events={events}
        handleEventSelect={(event) => setSelectedBooking(event)}
        eventPropGetter={(event) => ({
          style: {
            backgroundColor: event.bgColor,
            borderColor: event.bgColor,
            color: event.textColor,
          },
        })}
      />
      <ColourKey
        items={UserBookingComputedStatusOptions}
        activeItems={activeColourKeyItems}
        updateActiveItems={setActiveColourKeyItems}
      />
      <Modal
        show={!!selectedBooking}
        onHide={() => setSelectedBooking(null)}
        aria-labelledby="website-booking-calendar-modal-title"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="website-booking-calendar-modal-title" as="h5">
            {selectedBooking?.title}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedBooking && (
            <>
              {!!selectedBooking.booking && (
                <Display
                  entity={LocalBookingExtendedView}
                  keyValue={String(selectedBooking.booking.ticket_booking_id)}
                  row={selectedBooking.booking}
                />
              )}
              {!selectedBooking.booking && (
                <Row>
                  <DisplayFieldBlock
                    fieldId="start"
                    field={{ ...EventHostView.fields.start_at, formColumnWidths: { sm: 12 } }}
                    value={selectedBooking.start.toISOString()}
                  />
                  <DisplayFieldBlock
                    fieldId="end"
                    field={{ ...EventHostView.fields.end_at, formColumnWidths: { sm: 12 } }}
                    value={selectedBooking.end.toISOString()}
                  />
                </Row>
              )}
              <Stack direction="horizontal" gap={2}>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => {
                    navigate(formatEventUrl(selectedBooking.title as string, selectedBooking.id));
                  }}
                >
                  Go to Event
                </Button>
              </Stack>
            </>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};
